import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { GoalEditorProps } from '@web-app/components/GoalEditor/types';
import ConfirmModal from '@web-app/components/Modals/Confirm';
import { FC, useEffect, useState } from 'react';
import { MdOutlineDeleteOutline } from 'react-icons/md';
import ResizeTextarea from 'react-textarea-autosize';

import ActionableInterventionInput from '../ActionableInterventionInput';
import {
  ACTIONABLE_INTERVENTION_MAX_LENGTH,
  CLIENT_GOAL_MAX_LENGTH,
  COMMENTS_MAX_LENGTH,
} from './consts';

const GoalEditor: FC<GoalEditorProps> = ({ notes, setNotes }) => {
  const [clientGoal, setClientGoal] = useState<string>(
    notes.currentGlobalInterventions?.clientGoalsAndIntentions ?? '',
  );
  const [originalClientGoal, setOriginalClientGoal] =
    useState<string>(clientGoal);
  const [actionableIntervention, setActionableIntervention] = useState<string>(
    notes.currentGlobalInterventions?.actionableIntervention ?? '',
  );
  const [originalActionableIntervention, setOriginalActionableIntervention] =
    useState<string>(actionableIntervention);
  const [comments, setComments] = useState<string>(
    notes.currentGlobalInterventions?.comments ?? '',
  );
  const [originalComments, setOriginalComments] = useState<string>(comments);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const clearGoalDetails = () => {
    setClientGoal('');
    setOriginalClientGoal('');
    setActionableIntervention('');
    setOriginalActionableIntervention('');
    setComments('');
    setOriginalComments('');
  };

  useEffect(() => {
    setIsEditing(notes.currentlySelectedGoalIndex !== null);
  }, [notes]);

  useEffect(() => {
    if (notes.currentlySelectedGoalIndex !== null) {
      const initialClientGoal =
        notes.selectedClientGoalsAndInterventions[
          notes.currentlySelectedGoalIndex
        ]?.clientGoalsAndIntentions ?? '';
      setClientGoal(initialClientGoal);
      setOriginalClientGoal(initialClientGoal);

      const initialActionableIntervention =
        notes.selectedClientGoalsAndInterventions[
          notes.currentlySelectedGoalIndex
        ]?.actionableIntervention ?? '';
      setActionableIntervention(initialActionableIntervention);
      setOriginalActionableIntervention(initialActionableIntervention);

      const initialComments =
        notes.selectedClientGoalsAndInterventions[
          notes.currentlySelectedGoalIndex
        ]?.comments ?? '';
      setComments(initialComments);
      setOriginalComments(initialComments);
    }
  }, [notes.currentlySelectedGoalIndex]);

  useEffect(() => {
    if (
      notes?.currentGlobalInterventions?.clientGoalsAndIntentions !==
        clientGoal ||
      notes?.currentGlobalInterventions?.actionableIntervention !==
        actionableIntervention ||
      notes?.currentGlobalInterventions?.comments !== comments
    ) {
      setNotes({
        currentGlobalInterventions: {
          ...notes.currentGlobalInterventions,
          clientGoalsAndIntentions: clientGoal,
          actionableIntervention,
          comments,
        },
      });
    }
  }, [clientGoal, actionableIntervention, comments]);

  const addNewGoal = () => {
    const newCurrentGlobalInterventions = {
      ...notes.currentGlobalInterventions,
      actionableIntervention: actionableIntervention,
      clientGoalsAndIntentions: clientGoal,
      comments: comments,
    };
    setNotes({
      currentGlobalInterventions: {
        actionableIntervention: '',
        clientGoalsAndIntentions: '',
        comments: '',
      },
      selectedClientGoalsAndInterventions: [
        ...notes.selectedClientGoalsAndInterventions,
        { ...newCurrentGlobalInterventions },
      ],
      currentlySelectedGoalIndex: null,
      isGoalsAndInterventionsEditorOpen: false,
    });
    clearGoalDetails();
  };

  const updateExistingGoal = () => {
    if (
      notes.currentlySelectedGoalIndex === null ||
      notes.currentlySelectedGoalIndex >=
        notes.selectedClientGoalsAndInterventions.length
    ) {
      throw new Error('Attempting to update goal but cannot find it.');
    }

    const selectedGoalsCopy = [...notes.selectedClientGoalsAndInterventions];
    selectedGoalsCopy[notes.currentlySelectedGoalIndex] = {
      actionableIntervention: actionableIntervention,
      clientGoalsAndIntentions: clientGoal,
      comments: comments,
    };

    setNotes({
      currentGlobalInterventions: {
        actionableIntervention: '',
        clientGoalsAndIntentions: '',
        comments: '',
      },
      selectedClientGoalsAndInterventions: selectedGoalsCopy,
      currentlySelectedGoalIndex: null,
      isGoalsAndInterventionsEditorOpen: false,
    });
    clearGoalDetails();
  };

  const deleteGoal = () => {
    setNotes({
      currentGlobalInterventions: {
        clientGoalsAndIntentions: '',
        actionableIntervention: '',
        comments: '',
      },
      selectedClientGoalsAndInterventions:
        notes.selectedClientGoalsAndInterventions.filter(
          (v, index) => index !== notes.currentlySelectedGoalIndex,
        ),
      currentlySelectedGoalIndex: null,
      isGoalsAndInterventionsEditorOpen: false,
    });
    clearGoalDetails();
  };

  const handleCancelClick = () => {
    setNotes({
      currentGlobalInterventions: {
        actionableIntervention: '',
        clientGoalsAndIntentions: '',
        comments: '',
      },
      currentlySelectedGoalIndex: null,
      isGoalsAndInterventionsEditorOpen: false,
    });
    clearGoalDetails();
  };

  const isDirty = () => {
    return (
      clientGoal !== originalClientGoal ||
      actionableIntervention !== originalActionableIntervention ||
      comments !== originalComments
    );
  };

  const isInvalid = () => {
    return (
      clientGoal.length > CLIENT_GOAL_MAX_LENGTH ||
      actionableIntervention.length > ACTIONABLE_INTERVENTION_MAX_LENGTH ||
      comments.length > COMMENTS_MAX_LENGTH
    );
  };

  return (
    <Flex
      direction={'column'}
      height={'100%'}
      justifyContent={'space-between'}
      className="amp-mask excluded-phi"
    >
      <Box>
        <Heading size="md" fontWeight="300">
          {isEditing ? 'Edit goal' : 'Add a goal'}
        </Heading>
        <Box
          paddingX={['0px', '0px', '12px']}
          marginTop={'24px'}
          data-testid="goal-editor-form"
        >
          <Text mb={'8px'}>Client goals/intentions</Text>
          <Input
            type="text"
            data-testid="client-goal-input"
            background="theme.white"
            placeholder="Clearly identify one goal"
            fontSize={'14px'}
            value={clientGoal}
            onChange={(e) => {
              setClientGoal(e.target.value);
            }}
            isInvalid={clientGoal.length > CLIENT_GOAL_MAX_LENGTH}
            _placeholder={{
              color: 'green.400',
              fontSize: '14px',
            }}
            errorBorderColor="red.500"
            focusBorderColor="border.brand"
            borderColor={'green.400'}
          />
          <Text
            textAlign={'right'}
            color={'green.400'}
            fontWeight={600}
            fontSize={'sm'}
            marginTop={'4px'}
          >
            {clientGoal.length}/{CLIENT_GOAL_MAX_LENGTH}
          </Text>
          <Text mt={'16px'} mb={'8px'}>
            Actionable intervention (NCP)
          </Text>
          <ActionableInterventionInput
            setActionableInterventionText={setActionableIntervention}
            value={actionableIntervention}
          />
          <Text
            textAlign={'right'}
            color={'green.400'}
            fontWeight={600}
            fontSize={'sm'}
            marginTop={'4px'}
          >
            {actionableIntervention.length}/{ACTIONABLE_INTERVENTION_MAX_LENGTH}
          </Text>
          <Text mt={'16px'} mb={'8px'}>
            Comments/Progress
          </Text>
          <Textarea
            background="theme.white"
            placeholder="Update progress of goals & interventions"
            data-testid="client-goal-comments-input"
            fontSize={'14px'}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            _placeholder={{
              color: 'green.400',
              fontSize: '14px',
            }}
            as={ResizeTextarea}
            isInvalid={comments.length > COMMENTS_MAX_LENGTH}
            errorBorderColor="red.500"
            focusBorderColor="border.brand"
            borderColor={'green.400'}
            rows={1}
            minRows={1}
            maxRows={10}
            resize={'none'}
          />
          <Text
            textAlign={'right'}
            color={'green.400'}
            fontWeight={600}
            fontSize={'sm'}
            marginTop={'4px'}
          >
            {comments.length}/{COMMENTS_MAX_LENGTH}
          </Text>
        </Box>
      </Box>
      <Flex
        justifyContent={isEditing ? 'space-between' : 'flex-end'}
        alignItems="center"
      >
        {isEditing && (
          <ConfirmModal
            text="Deleting a goal will remove it permanently. This action can’t be undone."
            onOk={deleteGoal}
          >
            <MdOutlineDeleteOutline
              size="24px"
              fill="text.brand"
              cursor="pointer"
              data-testid="delete-goal-button"
              onClick={() => {}}
            />
          </ConfirmModal>
        )}
        <Flex justifyContent="flex-end">
          <Button
            color="text.brand"
            background="white"
            fontWeight={500}
            borderColor="border.brand"
            borderWidth="2px"
            borderRadius="8px"
            marginRight={'12px'}
            width="90px"
            onClick={handleCancelClick}
            _hover={{ color: 'white', backgroundColor: 'green.500' }}
          >
            Cancel
          </Button>
          <Button
            background="green.500"
            color="white"
            fontWeight={500}
            borderRadius="8px"
            width="90px"
            onClick={isEditing ? updateExistingGoal : addNewGoal}
            _hover={{
              backgroundColor: 'white',
              color: 'green.500',
              borderColor: 'green.500',
              borderWidth: '2px',
            }}
            isDisabled={
              !clientGoal ||
              !actionableIntervention ||
              (isEditing && !isDirty()) ||
              isInvalid()
            }
            data-testid={isEditing ? 'update-goal-button' : 'add-goal-button'}
          >
            {isEditing ? 'Save' : 'Add goal'}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default GoalEditor;
